import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createConsumer, getConfig } from '@rails/actioncable';

// Types
import { CalendarMeeting } from 'Shared/types/CalendarMeeting.type';

// Utils
import { Moment, getCurrentMoment, formatDate } from 'Shared/utils/DateTime.utils';

// Selectors
import { getCalendarMeetingsByDay } from 'Shared/selectors/CalendarMeetings.selectors';

// Actions
import { fetchCalendarMeetings } from 'Shared/actions/CalendarMeetings.actions';

interface IProps {
  day?: Moment;
}

const cable = createConsumer(getConfig('url'));

export const useFetchCalendarMeetings = ({ day = getCurrentMoment() }: Partial<IProps> = {}) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [response, setResponse] = useState(null);

  const today = formatDate(day, 'YYYY-MM-DD');

  const isCalendarConnected = useSelector(state => state.data_sources.find(ds => ds.isCalendar && ds.connected));
  const resyncInProgress = !!response?.resync_in_progress;

  const calendarMeetings = useSelector(state => getCalendarMeetingsByDay(state, { day: today })) as CalendarMeeting[];

  const hasMeetings = calendarMeetings.length > 0;

  const createCalendarMeetingSyncChannel = useCallback(
    () =>
      cable.subscriptions.create(
        { channel: 'CalendarMeetingsSyncChannel' },
        {
          received: ({ sync_finished }) => {
            if (sync_finished) {
              setIsLoading(true);
              dispatch(fetchCalendarMeetings({ day: today })).then(response => {
                setResponse(response);
                setIsLoading(false);
              });
            }
          },
        }
      ),
    [dispatch]
  );

  const handleResyncCalendar = useCallback(() => {
    setIsLoading(true);
    dispatch(fetchCalendarMeetings({ day: today, forceResync: true })).then(response => {
      setResponse(response);
    });
  }, [dispatch, today]);

  useEffect(() => {
    if (isCalendarConnected) {
      dispatch(fetchCalendarMeetings({ day: today })).then(response => {
        setIsLoading(false);
        setResponse(response);
      });

      const calendarMeetingSyncChannel = createCalendarMeetingSyncChannel();

      return () => {
        if (calendarMeetingSyncChannel) calendarMeetingSyncChannel.unsubscribe();
      };
    }
  }, [isCalendarConnected]);

  return {
    isLoading,
    response,
    isCalendarConnected,
    calendarMeetings,
    resyncInProgress,
    hasMeetings,
    handleResyncCalendar,
  };
};
